/**
 * Running an op against a stored game state: the single entry point the backend (and in-process tools) use.
 */
import type { RuleContext } from './context';
import { RuleError } from './errors';
import { RULE_OPS } from './ops/index';
import type { OpRegistry } from './registry';
import { decodeState, encodeState, StateFormatError, type StoredGameState } from './state';

export interface ExecutedOp {
  /** The new stored state (same format as the input). */
  gameState: StoredGameState;
  /** The op's JSON-serializable result. */
  result: unknown;
}

/**
 * Parses the arguments, decodes `gameState`, runs the op and encodes the new state. The input document is never
 * modified. Throws RuleError: 'unknown_op', 'bad_args', 'bad_state' or the op's own precondition reason.
 */
export function executeOp(opName: string, gameState: unknown, args: unknown, ctx: RuleContext, registry: OpRegistry = RULE_OPS): ExecutedOp {
  const op = Object.prototype.hasOwnProperty.call(registry, opName) ? registry[opName] : undefined;
  if (!op) throw new RuleError('unknown_op', { op: opName });
  const parsed = op.parseArgs(args);
  let state;
  try {
    state = decodeState(gameState);
  } catch (e) {
    if (e instanceof StateFormatError) throw new RuleError('bad_state', { problem: e.message });
    throw e;
  }
  const out = op.run(state, parsed, ctx);
  return { gameState: encodeState(out.state), result: out.result };
}
